import {Component, inject, OnInit, OnDestroy, signal, effect} from '@angular/core';
import {SidebarModule} from "@coreui/angular";
import {HasPermissionDirective} from "../../../directives/permissions/has-permission";
import {MatIcon} from "@angular/material/icon";
import {MatTooltip} from "@angular/material/tooltip";
import {RouterLink} from "@angular/router";
import {AsyncPipe} from "@angular/common";
import {MatListModule} from "@angular/material/list";
import {MatBadge} from "@angular/material/badge";
import {Observable, Subject, takeUntil} from "rxjs";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";
import {PermissionTypes} from "../../../enum/PermissionTypes";
import {GameService} from "../../../services/game.service";
import {SupportRequestService} from "../../../services/support-request.service";
import {ActiveUserService} from "../../../services/active-user.service";
import {MatExpansionModule} from "@angular/material/expansion";
import {OidcService} from "../../../services/oidc.service";
import {MatLine} from "@angular/material/core";
import {PermissionsService} from "../../../services/permissions.service";

@Component({
  selector: 'app-game-admin-side-nav',
  standalone: true,
  imports: [
    MatListModule,
    AsyncPipe,
    RouterLink,
    MatTooltip,
    MatIcon,
    HasPermissionDirective,
    MatBadge,
    SidebarModule,
    MatExpansionModule,
    MatLine
  ],
  templateUrl: './game-admin-side-nav.component.html',
  styleUrl: '../side-nav.component.scss'
})
export class GameAdminSideNavComponent implements OnInit, OnDestroy {
  private oidcService = inject(OidcService);
  private gameService = inject(GameService);
  private activeUserService = inject(ActiveUserService);
  private supportRequestService = inject(SupportRequestService);
  private permissionsService = inject(PermissionsService);

  private destroy$ = new Subject<void>();

  public activeGameId$ = this.gameService.selectActiveGameId();
  public gameDrawnManually$ = this.gameService.selectActiveGameDrawnManually();
  public availableGamesToUser$: Observable<IGameQueryResult[]> = this.gameService.selectAllAvailableGames();
  sideNavShouldOpen: boolean = true;
  public permissionTypes = PermissionTypes;

  // Signal for assigned support requests count
  public assignedSupportRequestsCount = signal<number>(0);

  constructor() {
    // React to game changes
    effect(() => {
      const game = this.gameService.activeGame();
      if (game?.Id) {
        this.refreshAssignedRequestsCount();
      }
    });
  }

  ngOnInit(): void {
    // Initial fetch
    this.refreshAssignedRequestsCount();

    // Listen for assignment changes (assign, resolve, escalate)
    this.supportRequestService.assignmentChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.refreshAssignedRequestsCount();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private refreshAssignedRequestsCount(): void {
    const userId = this.activeUserService.activeUser()?.Id;
    const gameId = this.gameService.activeGame()?.Id;

    if (!userId || !gameId) return;

    // Only fetch if user has permission to view support requests
    if (!this.permissionsService.userHasPermission(PermissionTypes.ViewSupportRequests)) {
      return;
    }

    this.supportRequestService.fetchMyAssignedRequests(userId, gameId).subscribe({
      next: (requests) => {
        // Only count unresolved tickets
        const unresolvedCount = requests.filter(r => !r.Resolved).length;
        this.assignedSupportRequestsCount.set(unresolvedCount);
      },
      error: () => {
        // Silently fail - don't show error for background refresh
      }
    });
  }

  onLogoutClick(): void {
    this.oidcService.logout();
  }
}
